import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Alert,
  TextField
} from "@mui/material";
import {
  Meter
} from "../../redux/residentPortalApi";
import { capitalise } from "../../utils/stringUtil";
import PaynowPaymentForm from "./PaynowPaymentForm";
import paynowService from "../../utils/paynowService";
import { getPaymentReturnUrl } from '../../utils/paymentReturnUrl';

interface TopUpModalProps {
  selectedMeter?: Meter | null;
  purpose: string;
  propertyId: string;
}

const TopUpModal: React.FC<TopUpModalProps> = ({ selectedMeter, propertyId, purpose }) => {
  const [amount, setAmount] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [pollUrl, setPollUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topUpFeedback, setTopUpFeedback] = useState<{
    topUpStatus?: string;
    topUpError?: string;
    meterProvider?: string;
    meterId?: string;
    metaInfo?: unknown;
  } | null>(null);

  const handleCreateIntent = async () => {
    // Validate inputs
    if (!amount) {
      setError('Please enter an amount');
      return;
    }

    if (!email) {
      setError('Please enter your email');
      return;
    }

    if (!phone) {
      setError('Please enter your phone number');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const currentUrl = getPaymentReturnUrl(propertyId);
      const reference = `${propertyId}-${Date.now()}`;
      const currency = 'USD';
      const result = await paynowService.initiatePayment(
        Number(amount),
        currency,
        reference,
        email,
        phone,
        propertyId,
        purpose,
        currentUrl
      );

      if (!result.success || !result.redirect) {
        setError(result.error || 'Failed to initialize payment. Please try again.');
        setIsLoading(false);
        return;
      }

      setReference(result.reference || reference);
      setRedirectUrl(result.redirect);
      if (result.pollUrl) {
        setPollUrl(result.pollUrl);
      }

    } catch (err) {
      console.error("Could not initialize payment:", err);
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (result?: {
    topUpStatus?: string;
    topUpError?: string;
    meterProvider?: string;
    meterId?: string;
    metaInfo?: unknown;
  }) => {
    setTopUpFeedback(result ?? null);
    setPaymentSuccess(true);
  };

  const numericAmount = Number(amount) * 100;
  const purposeText = purpose || (selectedMeter?.service ? `${selectedMeter.service} Meter` : '');

  return (
    <Box>
      {!redirectUrl ? (
        <Stack padding={5} spacing={5}>
          <Typography variant="h5">Enter Amount to Top Up {capitalise(purposeText)}</Typography>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">Amount</InputLabel>
            <OutlinedInput
              id="amount"
              startAdornment={<InputAdornment position="start">US $</InputAdornment>}
              label="Amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </FormControl>

          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            required
          />

          <TextField
            label="Phone Number"
            type="tel"
            fullWidth
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setError(null);
            }}
            required
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateIntent}
            disabled={!amount || !email || !phone || isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Pay"}
          </Button>
        </Stack>
      ) : paymentSuccess ? (
        <Box sx={{ mt: 2, p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="success.main" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography>
            Your payment for {capitalise(purposeText)} has been processed successfully.
          </Typography>
          {topUpFeedback?.topUpError && (
            <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>
                Meter top-up needs admin attention.
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {topUpFeedback.topUpError}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Provider: {topUpFeedback.meterProvider || 'unknown'} | Meter: {topUpFeedback.meterId || 'unknown'} | Status: {topUpFeedback.topUpStatus || 'unknown'}
              </Typography>
            </Alert>
          )}
        </Box>
      ) : (
        <PaynowPaymentForm
          amount={numericAmount}
          purpose={purposeText}
          propertyId={propertyId}
          reference={reference}
          redirectUrl={redirectUrl}
          pollUrl={pollUrl}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Box>
  );
};

export default TopUpModal;
